import type { FreePlanInformation } from '@/api/v1/auth/get-user-account-context/get-user-account-context.types'
import { useContextStore } from '@/stores/context'

/**
 * Access-state model, in priority order. Ex-customer ("reactivate") wins over
 * the free-plan states: once an account has paid, it is never shown free UI.
 */
export type AccessState =
  | 'subscribed'
  | 'reactivate'
  | 'free'
  | 'free-depleted'
  | 'no-plan'
  | 'none'

interface AccessStateResult {
  hasActiveSubscription: boolean
  isManaged: boolean
  freePlan: FreePlanInformation | null
  hasFreePlan: boolean
  planFeature: string
  accessState: AccessState
  isFormerPaidSubscriber: boolean
}

interface AccessStateInput {
  isManaged: boolean
  hasActiveSubscription: boolean
  hasHadPaidSubscription: boolean
  hasFreePlan: boolean
  freeEnvelopesRemaining: number
}

function deriveAccessState(input: AccessStateInput): AccessState {
  if (input.isManaged) return 'none'
  if (input.hasActiveSubscription) return 'subscribed'
  if (input.hasHadPaidSubscription) return 'reactivate'

  if (input.hasFreePlan) {
    return input.freeEnvelopesRemaining > 0 ? 'free' : 'free-depleted'
  }

  // Not managed, never subscribed and no free plan: an ineligible account
  // (e.g. a user's additional account).
  return 'no-plan'
}

export function useAccessState(): AccessStateResult {
  const accountContext = useContextStore((s) => s.userAccountContext)

  // Context not loaded yet — surface nothing (prevents a flash of the
  // "no plan" banner before the real account state is known).
  if (!accountContext) {
    return {
      hasActiveSubscription: false,
      isManaged: false,
      freePlan: null,
      hasFreePlan: false,
      planFeature: 'Pro',
      accessState: 'none',
      isFormerPaidSubscriber: false,
    }
  }

  const isManaged = accountContext.accountInformation.isManaged
  // Older persisted contexts (e.g. localStorage snapshots) may predate these
  // fields entirely, hence the `?? null` fallback for undefined.
  const freePlan = accountContext.freePlanInformation ?? null
  const hasFreePlan = freePlan !== null

  const accessState = deriveAccessState({
    isManaged,
    hasActiveSubscription: accountContext.hasActiveSubscription,
    hasHadPaidSubscription: accountContext.hasHadPaidSubscription,
    hasFreePlan,
    freeEnvelopesRemaining: freePlan?.envelopesRemaining ?? 0,
  })

  return {
    hasActiveSubscription: accountContext.hasActiveSubscription,
    isManaged,
    freePlan,
    hasFreePlan,
    planFeature: accountContext.balanceInformation.featureType,
    accessState,
    isFormerPaidSubscriber: accessState === 'reactivate',
  }
}
